import React, { useState, useEffect, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import { Collapse } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import Icon from '@mdi/react';
import { mdiAccountSearch, mdiCurrencyEur, mdiEarth, mdiDebugStepOver, mdiLaptop } from '@mdi/js';
import { resetFilters, setFilterSelectedValue, setComputedFilters } from '../reducers/enrollments';
import { resetEnrollments, setEnrollmentsLoading, fetchFilters, fetchEnrollments } from '../reducers/enrollments';
import Filter, { isActive } from '../partials/Filter';
import { activate } from './activate';

function getCurrentScolarYear() {
  const today = new Date();
  if (today.getMonth() < 8) {
    return (today.getFullYear() - 1) + '/' + today.getFullYear();
  } else {
    return today.getFullYear() + '/' + (today.getFullYear() + 1);
  }
}

// Each filter: its state name, the key sent to /enrollments, its label.
const groups = [
  { key: 'basic', title: 'Basique', icon: mdiAccountSearch, filters: [
    [ 'years', 'year', 'Année scolaire' ],
    [ 'schools', 'school', 'École' ],
    [ 'programs', 'program', 'Programme' ],
    [ 'sections', 'section', 'Section' ],
    [ 'specialities', 'speciality', 'Titre' ],
    [ 'campuses', 'campus', 'Ville' ],
    [ 'promos', 'promo', 'Promotion' ],
    [ 'admStatuses', 'admStatus', 'Statut ADM' ],
    [ 'pedagoStatuses', 'pedagoStatus', 'Statut pédago' ],
  ] },
  { key: 'scholarship', title: 'Bourses', icon: mdiCurrencyEur, filters: [
    [ 'scholarships', 'scholarship', 'Boursier CROUS' ],
    [ 'scholarshipsLevels', 'scholarshipLevel', 'Echelon CROUS' ],
    [ 'specialScholarships', 'specialScholarship', 'Bourses spéciales' ],
  ] },
  { key: 'international', title: 'International', icon: mdiEarth, filters: [
    [ 'destinations', 'destination', 'Destination' ],
  ] },
  { key: 'future', title: 'Devenir', icon: mdiDebugStepOver, filters: [
    [ 'futures', 'future', 'Devenir' ],
    [ 'futureDestinations', 'futureDestination', 'Destination' ],
    [ 'reenrollmentStatuses', 'reenrollmentStatus', 'Réinscription lancée' ],
    [ 'graduated', 'graduated', 'Titré/Diplômé' ],
  ] },
  { key: 'others', title: 'Autres', icon: mdiLaptop, filters: [
    [ 'seniorities', 'seniority', 'Ancienneté' ],
    [ 'financialStatuses', 'financialStatus', 'Statut financier' ],
  ] },
];
const allFilters = groups.flatMap((group) => group.filters);

function Sidebar() {

  const search = useSelector((state) => state.enrollments.search);
  const filtersLoaded = useSelector((state) => state.enrollments.filtersLoaded);
  const filtersDisabled = useSelector((state) => state.enrollments.filtersDisabled);
  const filters = useSelector((state) => state.enrollments.filters);
  const sorting = useSelector((state) => state.enrollments.sorting);
  const dispatch = useDispatch();

  const iconSize = 0.75;

  const [open, setOpen] = useState({ basic: true });
  const initialized = useRef(false);

  useEffect(() => {
    if (filtersLoaded === false) {
      dispatch(fetchFilters());
    }
  }, [filtersLoaded, dispatch]);

  useEffect(() => {
    if (filtersLoaded && !filtersDisabled && filters && !initialized.current) {
      initialized.current = true;
      filterUpdate({ target: { name: 'years', value: getCurrentScolarYear() } });
    }
    // filterUpdate is left out: it is rebuilt every render, and the initialized guard already makes this run once.
    // react-doctor-disable-next-line react-doctor/exhaustive-deps
  }, [filtersLoaded, filtersDisabled, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // update: the filter just changed, or null for none at all.
  function generateFilters(update) {
    const computedFilters = Object.fromEntries(allFilters.map(([ name, key ]) => [ key, (update === null) ? '' : (update?.name === name) ? update.value : filters[name].selected ]));
    dispatch(setComputedFilters(computedFilters));
    return computedFilters;
  }

  function filterUpdate(event) {
    if (filtersLoaded === false) return false;
    dispatch(setFilterSelectedValue({name: event.target.name, value: event.target.value}));
    dispatch(setEnrollmentsLoading());
    dispatch(resetEnrollments());
    dispatch(fetchEnrollments({ search, filters: generateFilters(event.target), sorting }));
  }

  // filterUpdate would read the filters from before the reset: one request, built here, on the current year.
  function reset() {
    const year = getCurrentScolarYear();
    dispatch(resetFilters());
    dispatch(setFilterSelectedValue({ name: 'years', value: year }));
    dispatch(setEnrollmentsLoading());
    dispatch(resetEnrollments());
    const computedFilters = { ...generateFilters(null), year };
    dispatch(setComputedFilters(computedFilters));
    dispatch(fetchEnrollments({ search, filters: computedFilters, sorting }));
  }

  return (
    <nav className="sidebar sidebar-offcanvas" id="sidebar">
      <ul className="nav">
        <li className="nav-item nav-category">
          <span className="nav-link">Filtres</span>
        </li>
        {groups.map(({ key, title, icon, filters: items }) => {
          const count = items.filter(([ name ]) => isActive(filters[name].selected)).length;
          return (
            <li key={key} className="nav-item menu-items">
              <div className={ open[key] ? 'nav-link menu-expanded' : 'nav-link' } { ...activate(() => setOpen({ ...open, [key]: !open[key] })) } aria-expanded={ open[key] === true } data-toggle="collapse">
                <span className="menu-icon">
                  <div className={ (count > 0) ? 'icon-container d-block' : 'icon-container' }>
                    <div className="filters-count">{count}</div>
                  </div>
                  <Icon path={icon} size={iconSize} />
                </span>
                <span className="menu-title">{title}</span>
                <i className="menu-arrow"></i>
              </div>
              <Collapse in={ open[key] === true } id={`${key}-filters`}>
                <ul className="nav flex-column sub-menu">
                  {items.map(([ name, , label ]) => <Filter key={name} id={`${name}Select`} label={label} name={name} onChange={filterUpdate} />)}
                </ul>
              </Collapse>
            </li>
          );
        })}
        <li className="nav-item menu-items nav-button">
          <div className="form-group">
            <div id="reinit-filters">
              <button className="btn btn-outline-light btn-fw" type="button" onClick={ () => reset() }> Réinitialiser </button>
            </div>
          </div>
        </li>
      </ul>
    </nav>
  );
}

export default withRouter(Sidebar);